import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { ChineseService, ChineseLesson, ChineseWord, LessonSummary } from '../../services/chineseService';
import NotesService from '../../services/notesService';
import BlockService from '../../services/blockService';
import PageHeader from '../common/PageHeader';
import GenericHistorySelector from '../shared/GenericHistorySelector';
import GenericPastLessons from '../shared/GenericPastLessons';
import './ChineseReading.css';

interface ChineseReadingProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type PinyinMode = 'hidden' | 'visible';
type LoadingStage = 'idle' | 'searching' | 'text' | 'vocabulary';
type TabType = 'text' | 'vocabulary';
type ViewMode = 'current' | 'past-list' | 'past-lesson';

const ChineseReading: React.FC<ChineseReadingProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<ChineseLesson | null>(null);
  const [selectedWord, setSelectedWord] = useState<ChineseWord | null>(null);
  const [linkedNoteId, setLinkedNoteId] = useState<string | null>(null);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [pinyinMode, setPinyinMode] = useState<PinyinMode>('hidden');
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('text');
  const [showMenu, setShowMenu] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [selectedPastLesson, setSelectedPastLesson] = useState<ChineseLesson | null>(null);
  const [showHistorySelector, setShowHistorySelector] = useState(false);
  const [pastLessonsList, setPastLessonsList] = useState<LessonSummary[]>([]);
  const [showImageGeneratorModal, setShowImageGeneratorModal] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [isGeneratingImageOnly, setIsGeneratingImageOnly] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [newsTopics, setNewsTopics] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Load latest lesson from database on mount
  useEffect(() => {
    loadLatestLesson();
  }, []);

  const loadLatestLesson = async () => {
    try {
      const result = await ChineseService.getPastLessons(1);
      if (result.lessons.length > 0) {
        const latestLesson = await ChineseService.getLessonById(result.lessons[0].lesson_id);
        setLesson(latestLesson);
      }
    } catch (err) {
      console.error('Error loading latest lesson:', err);
      // It's okay if there are no lessons yet
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleOpenGenerateLesson = async () => {
    try {
      setShowMenu(false);
      // Fetch past lessons for the selector
      const result = await ChineseService.getPastLessons(100);
      setPastLessonsList(result.lessons);
      setShowHistorySelector(true);
    } catch (err) {
      console.error('Error loading past lessons:', err);
      // Open selector anyway, it will show empty state
      setPastLessonsList([]);
      setShowHistorySelector(true);
    }
  };

  const handleGenerateLesson = async (selectedIds: string[], customInstruction: string) => {
    try {
      setShowHistorySelector(false);
      setLoadingStage('text');
      setError(null);
      setSelectedWord(null);
      
      // Generate lesson with progress tracking
      const newLesson = await ChineseService.generateLesson(
        selectedIds,
        customInstruction || undefined,
        (stage) => {
          setLoadingStage(stage);
        }
      );
      
      setLesson(newLesson);
      setLoadingStage('idle');
      setActiveTab('text');
      setViewMode('current');
    } catch (err) {
      console.error('Error generating lesson:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate lesson. Please try again.');
      setLoadingStage('idle');
    }
  };

  const handleOpenGenerateFromNews = async () => {
    try {
      setShowMenu(false);
      // Fetch past lessons for the selector
      const result = await ChineseService.getPastLessons(100);
      setPastLessonsList(result.lessons);
      setShowNewsModal(true);
    } catch (err) {
      console.error('Error loading past lessons:', err);
      // Open modal anyway, it will show empty state
      setPastLessonsList([]);
      setShowNewsModal(true);
    }
  };

  const handleGenerateLessonFromNews = async (selectedIds: string[]) => {
    try {
      setShowNewsModal(false);
      setLoadingStage('searching');
      setError(null);
      setSelectedWord(null);
      
      // Generate news-based lesson with progress tracking
      const newLesson = await ChineseService.generateLessonFromNews(
        newsTopics.trim() || undefined,
        selectedIds,
        (stage) => {
          setLoadingStage(stage);
        }
      );
      
      setLesson(newLesson);
      setLoadingStage('idle');
      setActiveTab('text');
      setViewMode('current');
      setNewsTopics(''); // Reset news topics
    } catch (err) {
      console.error('Error generating news-based lesson:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate news-based lesson. Please try again.');
      setLoadingStage('idle');
    }
  };

  const handleOpenPastLessons = () => {
    setShowMenu(false);
    setViewMode('past-list');
  };

  const handleSelectPastLesson = (pastLesson: ChineseLesson) => {
    setSelectedPastLesson(pastLesson);
    setViewMode('past-lesson');
    setActiveTab('text');
  };

  const handleBackToCurrentLesson = () => {
    setViewMode('current');
    setSelectedPastLesson(null);
  };

  const togglePinyinMode = () => {
    setPinyinMode(current => current === 'hidden' ? 'visible' : 'hidden');
    setShowMenu(false);
  };

  const handleOpenGenerateImage = () => {
    setShowMenu(false);
    const currentLesson = getCurrentLesson();
    if (currentLesson) {
      setImagePrompt(currentLesson.lesson_objective || '');
      setShowImageGeneratorModal(true);
    }
  };

  const handleGenerateImageOnly = async () => {
    const currentLesson = getCurrentLesson();
    if (!currentLesson?.lesson_id || !imagePrompt.trim()) return;

    try {
      setIsGeneratingImageOnly(true);
      setError(null);

      // Generate image
      const imageData = await ChineseService.generateLessonImage(
        currentLesson.lesson_id,
        imagePrompt.trim()
      );

      // Upload to Firebase and save URL
      const imageUrl = await ChineseService.uploadLessonImage(
        currentLesson.lesson_id,
        imageData.image,
        imageData.mimeType,
        imagePrompt.trim()
      );

      // Update lesson with image URL
      const updatedLesson = { ...currentLesson, image_url: imageUrl, image_prompt: imagePrompt.trim() };
      
      // Update the appropriate lesson state
      if (viewMode === 'past-lesson' && selectedPastLesson) {
        setSelectedPastLesson(updatedLesson);
      } else {
        setLesson(updatedLesson);
      }
      
      setShowImageGeneratorModal(false);
      setImagePrompt('');
    } catch (err) {
      console.error('Error generating image:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImageOnly(false);
    }
  };

  // Helper function to sanitize character for note_id
  const sanitizeNoteId = (character: string): string => {
    // Replace spaces and special characters, keep Chinese characters
    return character.replace(/[^\w\u4e00-\u9fff]/g, '-').toLowerCase();
  };

  const handleWordClick = (word: ChineseWord) => {
    setSelectedWord(word);
    
    // Check if note exists for this word
    const currentLesson = getCurrentLesson();
    if (currentLesson?.properties?.vocabularyNotes?.[word.character]) {
      setLinkedNoteId(currentLesson.properties.vocabularyNotes[word.character]);
    } else {
      setLinkedNoteId(null);
    }
  };

  const handleCreateNoteFromWord = async () => {
    if (!selectedWord || !user) return;

    const currentLesson = getCurrentLesson();
    if (!currentLesson?.lesson_id) {
      setError('No lesson available');
      return;
    }

    try {
      setIsCreatingNote(true);
      setError(null);

      // Generate note_id from character
      const noteId = `chinese-${sanitizeNoteId(selectedWord.character)}-${Date.now()}`;
      const noteTitle = selectedWord.character;

      // Create blocks with pinyin and English translation
      const pinyinBlock = await BlockService.createNoteBlock(
        user.id,
        selectedWord.pinyin
      );
      
      const englishBlock = await BlockService.createNoteBlock(
        user.id,
        selectedWord.english
      );

      // Create note with blocks
      const newNote = await NotesService.createNoteWithId(
        user.id,
        noteId,
        noteTitle,
        [
          { block_id: pinyinBlock.block_id, order: 0 },
          { block_id: englishBlock.block_id, order: 1 }
        ],
        {}
      );

      // Update lesson properties to link the note
      const currentProperties = currentLesson.properties || {};
      const vocabularyNotes = currentProperties.vocabularyNotes || {};
      vocabularyNotes[selectedWord.character] = noteId;

      const updatedProperties = {
        ...currentProperties,
        vocabularyNotes
      };

      // Update lesson properties
      const updatedLesson = await ChineseService.updateLessonProperties(
        currentLesson.lesson_id,
        updatedProperties
      );

      // Update the appropriate lesson state
      if (viewMode === 'past-lesson' && selectedPastLesson) {
        setSelectedPastLesson(updatedLesson);
      } else {
        setLesson(updatedLesson);
      }

      // Navigate to the note
      navigate(`/notes/${noteId}`);
    } catch (err) {
      console.error('Error creating note from word:', err);
      setError(err instanceof Error ? err.message : 'Failed to create note. Please try again.');
    } finally {
      setIsCreatingNote(false);
    }
  };

  const handleViewNote = () => {
    if (linkedNoteId) {
      navigate(`/notes/${linkedNoteId}`);
    }
  };

  // Get the current lesson being displayed
  const getCurrentLesson = () => {
    if (viewMode === 'past-lesson' && selectedPastLesson) {
      return selectedPastLesson;
    }
    return lesson;
  };

  // Split lesson text into clickable words
  const renderLessonText = () => {
    const currentLesson = getCurrentLesson();
    if (!currentLesson) return null;

    const { text, words } = currentLesson;
    const segments: JSX.Element[] = [];
    let currentIndex = 0;

    // Create a map for quick word lookup
    const wordMap = new Map<string, ChineseWord>();
    words.forEach(word => wordMap.set(word.character, word));

    // Try to match words in order they appear in text
    words.forEach((word, idx) => {
      const wordIndex = text.indexOf(word.character, currentIndex);
      
      if (wordIndex >= currentIndex) {
        // Add any text before this word
        if (wordIndex > currentIndex) {
          const beforeText = text.substring(currentIndex, wordIndex);
          segments.push(
            <span key={`text-${currentIndex}`} className="chinese-reading-text-plain">
              {beforeText}
            </span>
          );
        }

        // Add the clickable word
        segments.push(
          <span
            key={`word-${idx}`}
            className={`chinese-reading-word ${pinyinMode === 'visible' ? 'show-pinyin' : ''}`}
            onClick={() => handleWordClick(word)}
            data-pinyin={word.pinyin}
          >
            {word.character}
          </span>
        );

        currentIndex = wordIndex + word.character.length;
      }
    });

    // Add any remaining text
    if (currentIndex < text.length) {
      segments.push(
        <span key={`text-${currentIndex}`} className="chinese-reading-text-plain">
          {text.substring(currentIndex)}
        </span>
      );
    }

    return <div className="chinese-reading-text-content">{segments}</div>;
  };

  // Determine current lesson and view state
  const currentLesson = getCurrentLesson();
  const showBackButton = viewMode === 'past-lesson';

  return (
    <div className="chinese-reading-container">
      {/* If in past lessons list view, show GenericPastLessons component */}
      {viewMode === 'past-list' && (
        <GenericPastLessons
          service={ChineseService}
          onSelectLesson={handleSelectPastLesson}
          onClose={handleBackToCurrentLesson}
        />
      )}

      {/* Main lesson view */}
      {viewMode !== 'past-list' && (
        <>
      {/* Page Header */}
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          showBackButton ? (
            <button 
              className="page-header-icon-btn chinese-reading-back-btn"
              onClick={handleBackToCurrentLesson}
              aria-label="Back to current lesson"
            >
              ←
            </button>
          ) : (
            <button 
              className="page-header-icon-btn chinese-reading-back-btn"
              onClick={() => navigate('/chinese')}
              aria-label="Back to Chinese Learning"
            >
              ←
            </button>
          )
        }
        centerContent={
          <h1 className="page-header-center-title">
            {viewMode === 'past-lesson' ? 'Past Lesson' : 'Chinese Reading'}
          </h1>
        }
        rightActions={
          <div className="chinese-reading-menu-container" ref={menuRef}>
            <button 
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
              disabled={loadingStage !== 'idle'}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="chinese-reading-menu-dropdown">
                <button
                  className="chinese-reading-menu-item"
                  onClick={handleOpenGenerateLesson}
                >
                  ✨ Generate Standard Lesson
                </button>
                <button
                  className="chinese-reading-menu-item"
                  onClick={handleOpenGenerateFromNews}
                >
                  🌐 Generate from News
                </button>
                <button
                  className="chinese-reading-menu-item"
                  onClick={handleOpenGenerateImage}
                  disabled={!currentLesson}
                >
                  🖼️ Generate Image
                </button>
                <button
                  className="chinese-reading-menu-item"
                  onClick={handleOpenPastLessons}
                >
                  📚 Past Lessons
                </button>
                <button
                  className="chinese-reading-menu-item"
                  onClick={() => navigate('/chinese/practice')}
                >
                  ✏️ Translation Practice
                </button>
                {currentLesson && (
                  <button
                    className="chinese-reading-menu-item"
                    onClick={togglePinyinMode}
                  >
                    {pinyinMode === 'hidden' ? '👁️ Show Pinyin' : '👁️‍🗨️ Hide Pinyin'}
                  </button>
                )}
              </div>
            )}
          </div>
        }
      />

      {/* Main Content */}
      <div className="chinese-reading-main-content">
        {/* Loading State */}
        {loadingStage !== 'idle' && (
          <div className="chinese-reading-loading">
            <div className="chinese-reading-spinner-large"></div>
            <p className="chinese-reading-loading-text">
              {loadingStage === 'searching' && 'Step 1/3: Searching recent Chinese news...'}
              {loadingStage === 'text' && 'Step 2/3: Generating text...'}
              {loadingStage === 'vocabulary' && 'Step 3/3: Analyzing vocabulary...'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && loadingStage === 'idle' && (
          <div className="chinese-reading-error">
            <p>{error}</p>
          </div>
        )}

        {/* Lesson Content - Text Tab */}
        {currentLesson && loadingStage === 'idle' && activeTab === 'text' && (
          <div className="chinese-reading-chapter">
            <h1 className="chinese-reading-lesson-title">{currentLesson.lesson_name}</h1>
            <div className="chinese-reading-lesson-objective">
              {currentLesson.lesson_objective}
            </div>
            {currentLesson.image_url && (
              <img 
                src={currentLesson.image_url} 
                alt={currentLesson.lesson_name}
                className="chinese-reading-lesson-image"
              />
            )}
            <div className="chinese-reading-text-content">
              {renderLessonText()}
            </div>
            <div className="chinese-reading-instructions">
              💡 Click on any word to see its translation.
            </div>
          </div>
        )}

        {/* Vocabulary Tab */}
        {currentLesson && loadingStage === 'idle' && activeTab === 'vocabulary' && (
          <div className="chinese-reading-vocabulary-section">
            <h2 className="chinese-reading-vocab-section-header">Vocabulary ({currentLesson.words.length} words)</h2>
            <div className="chinese-reading-vocab-grid">
              {currentLesson.words.map((word, index) => (
                <button
                  key={index}
                  className={`chinese-reading-vocab-item ${selectedWord?.character === word.character ? 'active' : ''}`}
                  onClick={() => handleWordClick(word)}
                >
                  <span className="chinese-reading-vocab-char">{word.character}</span>
                  <span className="chinese-reading-vocab-pin">{word.pinyin}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Welcome Message */}
        {!currentLesson && loadingStage === 'idle' && !error && (
          <div className="chinese-reading-welcome">
            <h2>Welcome to Chinese Reading</h2>
            <p>Click the menu (⋮) in the top right and select "Generate Standard Lesson" to create an AI-powered Chinese reading lesson.</p>
            <div className="chinese-reading-features">
              <div className="chinese-reading-feature">
                <span className="chinese-reading-feature-icon">📖</span>
                <span>HSK 3-4 Level Reading</span>
              </div>
              <div className="chinese-reading-feature">
                <span className="chinese-reading-feature-icon">🔤</span>
                <span>Interactive Word Translations</span>
              </div>
              <div className="chinese-reading-feature">
                <span className="chinese-reading-feature-icon">🎯</span>
                <span>Pinyin & English Support</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Tab Bar */}
      {currentLesson && loadingStage === 'idle' && (
        <div className="chinese-reading-bottom-bar">
          <button
            className={`chinese-reading-tab-button ${activeTab === 'text' ? 'active' : ''}`}
            onClick={() => setActiveTab('text')}
          >
            Text
          </button>
          <button
            className={`chinese-reading-tab-button ${activeTab === 'vocabulary' ? 'active' : ''}`}
            onClick={() => setActiveTab('vocabulary')}
          >
            Vocabulary ({currentLesson.words.length})
          </button>
        </div>
      )}

      {/* History Selector Modal */}
      <GenericHistorySelector
        isOpen={showHistorySelector}
        pastLessons={pastLessonsList}
        onGenerate={handleGenerateLesson}
        onClose={() => setShowHistorySelector(false)}
        customInstructionPlaceholder="e.g., Shopping at a Chinese market, Ordering food at a restaurant..."
      />

      {/* Word Popup */}
      {selectedWord && (
        <>
          <div className="chinese-reading-popup-overlay" onClick={() => {
            setSelectedWord(null);
            setLinkedNoteId(null);
          }} />
          <div className="chinese-reading-word-popup">
            <button 
              className="chinese-reading-popup-close" 
              onClick={() => {
                setSelectedWord(null);
                setLinkedNoteId(null);
              }}
            >
              ×
            </button>
            <div className="chinese-reading-word-character">{selectedWord.character}</div>
            <div className="chinese-reading-word-pinyin">{selectedWord.pinyin}</div>
            <div className="chinese-reading-word-english">{selectedWord.english}</div>
            <div className="chinese-reading-word-actions">
              {linkedNoteId ? (
                <button
                  className="chinese-reading-word-action-btn chinese-reading-word-view-note-btn"
                  onClick={handleViewNote}
                  disabled={isCreatingNote}
                >
                  View Note
                </button>
              ) : (
                <button
                  className="chinese-reading-word-action-btn chinese-reading-word-create-note-btn"
                  onClick={handleCreateNoteFromWord}
                  disabled={isCreatingNote || !user}
                >
                  {isCreatingNote ? 'Creating...' : 'Create Note'}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* News Generation Modal */}
      {showNewsModal && (
        <>
          <div 
            className="chinese-reading-image-modal-overlay" 
            onClick={() => setShowNewsModal(false)}
          />
          <div className="chinese-reading-image-modal">
            <div className="chinese-reading-image-modal-header">
              <h2 className="chinese-reading-image-modal-title">Generate Lesson from News</h2>
              <button 
                className="chinese-reading-image-modal-close" 
                onClick={() => setShowNewsModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="chinese-reading-image-modal-content">
              <div className="chinese-reading-news-info">
                <p>🌐 AI will search for recent Chinese news from the last 24 hours and create a reading lesson based on real news stories.</p>
              </div>

              <label htmlFor="news-topics" className="chinese-reading-image-modal-label">
                News Topics (Optional)
              </label>
              <input
                id="news-topics"
                type="text"
                className="chinese-reading-image-modal-textarea"
                placeholder="e.g., technology, sports, culture, economics..."
                value={newsTopics}
                onChange={(e) => setNewsTopics(e.target.value)}
                style={{ height: '40px', resize: 'none', fontFamily: 'inherit' }}
              />
              <p className="chinese-reading-image-modal-hint">
                Leave empty to search for general Chinese news, or specify topics to focus on.
              </p>

              {/* Past Lessons Selection */}
              {pastLessonsList.length > 0 && (
                <div className="chinese-reading-news-past-lessons">
                  <label className="chinese-reading-image-modal-label">
                    Select past lessons to avoid repeating content (Optional)
                  </label>
                  <div className="chinese-reading-news-lessons-list">
                    {pastLessonsList.slice(0, 10).map((lesson) => (
                      <label key={lesson.lesson_id} className="chinese-reading-news-lesson-item">
                        <input
                          type="checkbox"
                          checked={false}
                          onChange={() => {}}
                          style={{ marginRight: '8px' }}
                        />
                        <span>{lesson.lesson_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="chinese-reading-image-modal-footer">
              <button 
                className="chinese-reading-image-modal-btn chinese-reading-image-modal-btn-cancel"
                onClick={() => setShowNewsModal(false)}
              >
                Cancel
              </button>
              <button 
                className="chinese-reading-image-modal-btn chinese-reading-image-modal-btn-generate"
                onClick={() => handleGenerateLessonFromNews([])}
              >
                Generate from News
              </button>
            </div>
          </div>
        </>
      )}

      {/* Image Generator Modal */}
      {showImageGeneratorModal && (
        <>
          <div 
            className="chinese-reading-image-modal-overlay" 
            onClick={() => !isGeneratingImageOnly && setShowImageGeneratorModal(false)}
          />
          <div className="chinese-reading-image-modal">
            <div className="chinese-reading-image-modal-header">
              <h2 className="chinese-reading-image-modal-title">Generate Image</h2>
              <button 
                className="chinese-reading-image-modal-close" 
                onClick={() => setShowImageGeneratorModal(false)}
                disabled={isGeneratingImageOnly}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="chinese-reading-image-modal-content">
              <label htmlFor="image-prompt" className="chinese-reading-image-modal-label">
                Image Description
              </label>
              <textarea
                id="image-prompt"
                className="chinese-reading-image-modal-textarea"
                placeholder="Describe the image you want to generate..."
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                disabled={isGeneratingImageOnly}
                rows={4}
              />
              <p className="chinese-reading-image-modal-hint">
                Provide a detailed description of the image you want to generate for this lesson.
              </p>
            </div>

            <div className="chinese-reading-image-modal-footer">
              <button 
                className="chinese-reading-image-modal-btn chinese-reading-image-modal-btn-cancel"
                onClick={() => setShowImageGeneratorModal(false)}
                disabled={isGeneratingImageOnly}
              >
                Cancel
              </button>
              <button 
                className="chinese-reading-image-modal-btn chinese-reading-image-modal-btn-generate"
                onClick={handleGenerateImageOnly}
                disabled={isGeneratingImageOnly || !imagePrompt.trim()}
              >
                {isGeneratingImageOnly ? 'Generating...' : 'Generate Image'}
              </button>
            </div>
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
};

export default ChineseReading;

